import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  input,
  OnChanges,
  output,
  SimpleChanges,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { IBookingPreview } from 'src/app/models/booking/bookingPreview';
import { IRoutePreview } from 'src/app/models';
import { BookingOrigin, ExpeditionStatus } from 'src/app/models/enums';
import { LiteralService, UtilsService } from 'src/app/services';
import { TooltipPosition } from 'src/app/shared/tooltip/tooltip-position';
import { images } from 'src/images';

@Component({
  selector: 'app-route-design-preview',
  templateUrl: './route-design-preview.component.html',
  styleUrl: './route-design-preview.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class RouteDesignPreviewComponent implements OnChanges {
  readonly routes = input<IRoutePreview[]>([]);
  readonly loadingBookings = input<Boolean>(false);
  readonly allowExcluding = input<Boolean>(false);
  readonly exludedTripIds = input<number[]>();
  onExcludeTripId = output<any>();
  onRouteSelect = output<IRoutePreview | null>();

  @ViewChild('routesRow') routesRow?: ElementRef<HTMLElement>;
  @ViewChild('measuringRow') measuringRow?: ElementRef<HTMLElement>;

  selectedRoute: IRoutePreview | null = null;
  visibleRoutes: IRoutePreview[] = [];
  isOverflowing = false;
  bookingsSeparatedByDatesAndTrips: any = {};
  filteredBookings: IBookingPreview[] = [];
  filteredRoutes: IRoutePreview[] = [];

  get hiddenRoutes(): IRoutePreview[] {
    return this.routes().filter(
      (r) => !this.visibleRoutes.some((v) => v.route.id === r.route.id),
    );
  }

  get moreOptionsOpened(): boolean {
    return this.hiddenRoutes.some(
      (r) => r.route.id === this.selectedRoute?.route?.id,
    );
  }

  loadingDates = false;
  datesList: any[] = [];
  selectedDate: string = moment(new Date()).format('YYYY-MM-DD');
  selectedTrip: any;

  expeditionStatus: ExpeditionStatus | null = null;

  public images = images;
  public BookingOrigin = BookingOrigin;
  public TooltipPosition = TooltipPosition;

  constructor(
    public utilsService: UtilsService,
    public router: Router,
    public literalService: LiteralService,
    public translateService: TranslateService,
    public cdr: ChangeDetectorRef,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['routes']) {
      this.filteredRoutes = changes['routes'].currentValue;
      const routes: IRoutePreview[] = changes['routes'].currentValue;
      this.selectedRoute = routes[0] ?? null;
      this.onRouteSelect.emit(this.selectedRoute);
      this.regroupByRoute();
      this.computeVisibleRoutes();
    }
  }

  @HostListener('window:resize')
  onResize(): void {
    this.computeVisibleRoutes();
  }

  private computeVisibleRoutes(): void {
    const routes = this.routes();
    if (routes.length === 0) {
      this.visibleRoutes = [];
      return;
    }
    setTimeout(() => {
      const container = this.routesRow?.nativeElement;
      const measuring = this.measuringRow?.nativeElement;
      if (!container || !measuring) {
        this.visibleRoutes = routes;
        return;
      }
      this.isOverflowing = measuring.offsetWidth > container.clientWidth;
      this.updateVisibleRoutes();
      this.cdr.detectChanges();
    });
  }

  private updateVisibleRoutes(): void {
    const routes = this.routes();
    if (!this.isOverflowing) {
      this.visibleRoutes = routes;
      return;
    }
    const windowSize = 3;
    const selectedIndex = Math.max(
      0,
      routes.findIndex((r) => r.route.id === this.selectedRoute?.route?.id),
    );
    const start = Math.min(
      selectedIndex,
      Math.max(0, routes.length - windowSize),
    );
    this.visibleRoutes = routes.slice(start, start + windowSize);
  }

  private regroupByRoute(): void {
    this.loadingDates = true;
    this.datesList = [];
    this.bookingsSeparatedByDatesAndTrips = {};

    (this.selectedRoute?.bookings ?? []).forEach((booking: IBookingPreview) => {
      const stringDate = moment(booking.availability.dateTime).format(
        'YYYY-MM-DD',
      );
      const tripId = booking.tripId;
      const dateIndex = this.datesList.findIndex(
        (date) => date.date === stringDate,
      );
      if (dateIndex !== -1) {
        const date = this.datesList[dateIndex];
        if (date.trips.some((trip: any) => trip.id === tripId)) {
          this.bookingsSeparatedByDatesAndTrips[stringDate][tripId].push(
            booking,
          );
        } else {
          this.datesList[dateIndex].trips.push({
            id: tripId,
            time: moment
              .parseZone(booking.availability.dateTime)
              .format('HH:mm'),
          });
          this.datesList[dateIndex].trips.sort((a: string, b: string) => a < b);
          this.bookingsSeparatedByDatesAndTrips[stringDate][tripId] = [booking];
        }
      } else {
        this.datesList.push({
          date: stringDate,
          trips: [
            {
              id: tripId,
              time: moment
                .parseZone(booking.availability.dateTime)
                .format('HH:mm'),
            },
          ],
        });
        this.datesList.sort(
          (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
        );
        this.bookingsSeparatedByDatesAndTrips[stringDate] = {};
        this.bookingsSeparatedByDatesAndTrips[stringDate][tripId] = [booking];
      }
    });

    if (this.datesList.length > 0) {
      this.selectedDate = this.datesList[0].date;
      this.selectedTrip = this.datesList[0].trips[0];
      this.filteredBookings =
        this.bookingsSeparatedByDatesAndTrips[this.selectedDate][
          this.selectedTrip.id
        ];
      this.expeditionStatus = this.filteredBookings[0].availability
        .expeditionStatus as ExpeditionStatus;
    } else {
      this.selectedDate = '';
      this.selectedTrip = null;
      this.filteredBookings = [];
      this.expeditionStatus = null;
    }

    this.loadingDates = false;
    this.cdr.detectChanges();
  }

  onRouteChange(route: IRoutePreview): void {
    this.selectedRoute = route;
    this.updateVisibleRoutes();
    this.onRouteSelect.emit(this.selectedRoute);
    this.regroupByRoute();
  }

  onRouteSearch(search: any) {
    this.filteredRoutes = this.routes().filter((r) =>
      r.route.name.toLowerCase().includes(search.target.value.toLowerCase()),
    );
  }

  formatDate(date: string) {
    return `${this.literalService.get(`calendar.longWeekDays.${moment(date).format('ddd').toLowerCase()}`, true)} ${moment(date).format('DD/MM')}`;
  }

  isArrowDisabled(previous: boolean) {
    if (this.datesList.length === 0) return true;
    const dateIndex = this.datesList.findIndex(
      (date) => date.date === this.selectedDate,
    );
    const tripIndex = this.datesList[dateIndex]?.trips.findIndex(
      (trip: any) => trip.id === this.selectedTrip.id,
    );

    if (previous) return tripIndex === 0;
    return tripIndex === this.datesList[dateIndex].trips.length - 1;
  }

  onDateChange(date: string) {
    this.selectedDate = date;
    const dateIndex = this.datesList.findIndex(
      (date) => date.date === this.selectedDate,
    );
    this.selectedTrip = this.datesList[dateIndex].trips[0];

    this.filteredBookings =
      this.bookingsSeparatedByDatesAndTrips[this.selectedDate][
        this.selectedTrip.id
      ];
  }

  onTripChange(previous: boolean) {
    if (this.isArrowDisabled(previous)) return;
    const dateIndex = this.datesList.findIndex(
      (date) => date.date === this.selectedDate,
    );
    const tripIndex = this.datesList[dateIndex]?.trips.findIndex(
      (trip: any) => trip.id === this.selectedTrip.id,
    );

    if (previous)
      this.selectedTrip = this.datesList[dateIndex].trips[tripIndex - 1];
    else this.selectedTrip = this.datesList[dateIndex].trips[tripIndex + 1];

    this.filteredBookings =
      this.bookingsSeparatedByDatesAndTrips[this.selectedDate][
        this.selectedTrip.id
      ];
  }

  closeDropdownOnFocusout(event: FocusEvent, el: HTMLDetailsElement) {
    if (!el.contains(event.relatedTarget as Node)) {
      setTimeout(() => el.removeAttribute('open'), 0);
    }
  }

  onChangeExcludedTrip(event: any) {
    const tripId = this.selectedTrip.id;
    const excludedTripIds = this.exludedTripIds() ?? [];

    const updatedIds = event.target.checked
      ? [...new Set([...excludedTripIds, tripId])]
      : excludedTripIds.filter((id) => id !== tripId);

    this.onExcludeTripId.emit(updatedIds);
  }
}
